import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { setLanguage } from '@/features/settings';

import { de } from '../locales/de';
import { en } from '../locales/en/';
import { es } from '../locales/es';
import { esMx } from '../locales/es-mx';
import { fr } from '../locales/fr';
import { hi } from '../locales/hi';
import { id } from '../locales/id';
import { it } from '../locales/it';
import { ja } from '../locales/ja';
import { ko } from '../locales/ko';
import { ms } from '../locales/ms';
import { nl } from '../locales/nl';
import { pt } from '../locales/pt';
import { ptBr } from '../locales/pt-br';
import { ru } from '../locales/ru';
import { sv } from '../locales/sv';
import { th } from '../locales/th';
import { uk } from '../locales/uk';
import { zh } from '../locales/zh';
import { store } from './store';

const LANGUAGE_STORAGE_KEY = 'i18nextLng';
const isDevelopment = import.meta.env.DEV;

const supportedLngs = [
  'en',
  'de',
  'es',
  'es-MX',
  'fr',
  'hi',
  'id',
  'it',
  'ja',
  'ko',
  'ms',
  'nl',
  'pt',
  'pt-BR',
  'ru',
  'sv',
  'th',
  'uk',
  'zh',
] as const;

type SupportedLanguage = (typeof supportedLngs)[number];

const resources = {
  en: { translation: en.common },
  de: { translation: de.common },
  es: { translation: es.common },
  'es-MX': { translation: esMx.common },
  fr: { translation: fr.common },
  hi: { translation: hi.common },
  id: { translation: id.common },
  it: { translation: it.common },
  ja: { translation: ja.common },
  ko: { translation: ko.common },
  ms: { translation: ms.common },
  nl: { translation: nl.common },
  pt: { translation: pt.common },
  'pt-BR': { translation: ptBr.common },
  ru: { translation: ru.common },
  sv: { translation: sv.common },
  th: { translation: th.common },
  uk: { translation: uk.common },
  zh: { translation: zh.common },
} satisfies Record<SupportedLanguage, { translation: Record<string, unknown> }>;

const isSupportedLanguage = (lng: string): lng is SupportedLanguage =>
  (supportedLngs as readonly string[]).includes(lng);

const normalizeLanguage = (lng: string): SupportedLanguage | null => {
  if (isSupportedLanguage(lng)) {
    return lng;
  }

  const caseInsensitiveMatch = supportedLngs.find(
    (supportedLng) => supportedLng.toLowerCase() === lng.toLowerCase(),
  );

  if (caseInsensitiveMatch !== undefined) {
    return caseInsensitiveMatch;
  }

  const baseLanguage = lng.split('-')[0];

  return isSupportedLanguage(baseLanguage) ? baseLanguage : null;
};

const syncLanguageState = (lng: string): void => {
  document.documentElement.setAttribute('lang', lng);
  store.dispatch(setLanguage(lng));
};

const applyStoredLanguage = async (): Promise<void> => {
  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

  if (storedLanguage == null) {
    return;
  }

  const normalizedLanguage = normalizeLanguage(storedLanguage);

  if (normalizedLanguage == null) {
    return;
  }

  if (i18n.resolvedLanguage !== normalizedLanguage) {
    await i18n.changeLanguage(normalizedLanguage);
    return;
  }

  syncLanguageState(normalizedLanguage);
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: false,
    supportedLngs: [...supportedLngs],
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false,
    },
    resources,
  });

syncLanguageState(i18n.resolvedLanguage ?? i18n.language);

i18n.on('languageChanged', (lng) => {
  syncLanguageState(lng);
});

if (typeof window !== 'undefined' && isDevelopment) {
  const originalSetItem = window.localStorage.setItem.bind(window.localStorage);

  window.localStorage.setItem = (key: string, value: string): void => {
    originalSetItem(key, value);

    if (key === LANGUAGE_STORAGE_KEY) {
      void applyStoredLanguage();
    }
  };

  window.addEventListener('storage', (event) => {
    if (event.key === LANGUAGE_STORAGE_KEY) {
      void applyStoredLanguage();
    }
  });

  window.addEventListener('focus', () => {
    void applyStoredLanguage();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void applyStoredLanguage();
    }
  });
}

export default i18n;
